import { useRef, useState, type ChangeEvent, type PointerEvent } from "react";
import { ImagePlus } from "lucide-react";
import { uploadCardTemplateImage } from "../lib/api";
import { imageCropSourceRect, type ImageCropPosition } from "../lib/templateMedia";
import type { CardTemplatePage } from "../types";

type ImagePage = Extract<CardTemplatePage, { type: "image" }>;

export interface ImageCropDraft {
  pageUid: string;
  file: File;
  url: string;
  width: number;
  height: number;
  position: ImageCropPosition;
}

export function TemplateImageEditor({ page, draft, otherDraftPage, onDraftSelect, onDraftMove, onDraftCenter, onDraftClear, onChange, onBusy, onError, disabled, uploadImageFile = uploadCardTemplateImage }: {
  page: ImagePage;
  draft: ImageCropDraft | null;
  otherDraftPage: number | null;
  onDraftSelect: (draft: ImageCropDraft) => void;
  onDraftMove: (deltaX: number, deltaY: number, frameSize: number) => void;
  onDraftCenter: () => void;
  onDraftClear: () => void;
  onChange: (page: ImagePage) => void;
  onBusy: (busy: boolean) => void;
  onError: (message: string) => void;
  disabled: boolean;
  uploadImageFile?: (file: File) => Promise<string>;
}) {
  const [uploading, setUploading] = useState(false);
  const lastPointer = useRef<{ x: number; y: number } | null>(null);
  const busy = disabled || uploading;

  const choose = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { onError("Image must be 5 MB or smaller"); return; }
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) { onError("Choose a JPEG, PNG, or WebP image"); return; }
    setUploading(true); onBusy(true);
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.src = url;
    try {
      await image.decode();
      if (!image.naturalWidth || !image.naturalHeight) throw new Error("This image could not be opened");
      onDraftSelect({ pageUid: page.uid, file, url, width: image.naturalWidth, height: image.naturalHeight, position: { x: 0.5, y: 0.5 } });
    } catch {
      URL.revokeObjectURL(url);
      onError("This image could not be opened. Choose another file.");
    } finally { setUploading(false); onBusy(false); }
  };

  const useCrop = async () => {
    if (!draft || busy) return;
    setUploading(true); onBusy(true);
    try {
      const image = new Image();
      image.src = draft.url;
      await image.decode();
      const canvas = document.createElement("canvas");
      canvas.width = 1024; canvas.height = 1024;
      const context = canvas.getContext("2d");
      if (!context) throw new Error("This browser could not crop the image");
      const rect = imageCropSourceRect(draft.width, draft.height, draft.position);
      context.drawImage(image, rect.x, rect.y, rect.side, rect.side, 0, 0, 1024, 1024);
      const mime = draft.file.type === "image/png" ? "image/png" : "image/jpeg";
      const blob = await new Promise<Blob>((resolve, reject) => canvas.toBlob((result) => result ? resolve(result) : reject(new Error("Could not save the crop")), mime, 0.88));
      const cropped = new File([blob], mime === "image/png" ? "template-image.png" : "template-image.jpg", { type: mime });
      const url = await uploadImageFile(cropped);
      onChange({ ...page, image: url });
      onDraftClear();
    } catch (error) { onError(error instanceof Error ? error.message : "Image crop failed"); }
    finally { setUploading(false); onBusy(false); }
  };

  const move = (event: PointerEvent<HTMLDivElement>) => {
    if (!lastPointer.current || !draft) return;
    const deltaX = event.clientX - lastPointer.current.x;
    const deltaY = event.clientY - lastPointer.current.y;
    lastPointer.current = { x: event.clientX, y: event.clientY };
    onDraftMove(deltaX, deltaY, event.currentTarget.clientWidth);
  };

  return <div className="template-editor-grid">
    <div className="template-fields template-media-control">
      <label className="template-upload"><ImagePlus size={17} />{uploading ? "Working on image…" : page.image ? "Replace image" : "Upload image"}<input type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => void choose(event)} disabled={busy || otherDraftPage !== null} /></label>
      <p className="muted">JPEG, PNG, or WebP · up to 5 MB. Choose an image, drag it in the square, then use the crop.</p>
      {otherDraftPage !== null ? <p className="template-crop-hint">Finish the crop on Page {otherDraftPage} before choosing another image.</p> : null}
      {draft ? <div className="template-inline-actions"><button type="button" className="secondary-button" onClick={onDraftCenter} disabled={busy}>Center image</button><button type="button" className="primary-button" onClick={() => void useCrop()} disabled={busy}>{uploading ? "Uploading crop…" : "Use this crop"}</button></div> : page.image ? <p className="template-crop-hint">This square is the image saved for the phone. Upload a replacement to adjust its crop.</p> : null}
    </div>
    <div className="template-inline-preview"><p className="section-label">{draft ? "POSITION IMAGE" : "PAGE PREVIEW"}</p>
      <div className={`template-image-crop-stage ${draft ? "is-dragging-ready" : ""}`} role="group" tabIndex={draft && !busy ? 0 : undefined} aria-label={draft ? "Image crop. Drag or use arrow keys to position the image." : "Image page preview"}
        onPointerDown={(event) => { if (!draft || busy) return; event.currentTarget.setPointerCapture(event.pointerId); lastPointer.current = { x: event.clientX, y: event.clientY }; }}
        onPointerMove={move}
        onPointerUp={() => { lastPointer.current = null; }}
        onPointerCancel={() => { lastPointer.current = null; }}
        onKeyDown={(event) => { if (!draft || busy || !["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) return; event.preventDefault(); const step = event.shiftKey ? 30 : 8; onDraftMove(event.key === "ArrowLeft" ? -step : event.key === "ArrowRight" ? step : 0, event.key === "ArrowUp" ? -step : event.key === "ArrowDown" ? step : 0, event.currentTarget.clientWidth); }}>
        {draft ? <img src={draft.url} alt="Image being cropped" draggable={false} style={{ objectPosition: `${draft.position.x * 100}% ${draft.position.y * 100}%` }} /> : page.image ? <img src={page.image} alt="Saved image page" /> : <span>Add an image to see its crop</span>}
      </div>
      <p className="muted">{draft ? "Drag to choose the visible area. Arrow keys also move the image; Shift moves farther." : "The phone shows this square image."}</p>
    </div>
  </div>;
}
